import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, CheckCircle, Target, ArrowRight, Zap, Star, Crown, Rocket, Building2 } from 'lucide-react';
import { CJMSClient, cjmsApi } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface GoalUpsellProps {
    client: CJMSClient;
}

const PROGRAM_TIERS = [
    {
        id: 'free',
        name: 'Free 1-Pager',
        tagline: 'Get your first website free',
        price: 0,
        priceLabel: 'FREE',
        period: '',
        icon: Target,
        color: 'slate',
        features: [
            '1-page professional website',
            'Lifetime hosting included',
            'Mobile responsive design',
            'Basic SEO optimization',
            'Unlock by referring 5 friends',
        ],
        cta: 'Refer Friends',
        popular: false,
    },
    {
        id: 'goal10',
        name: 'Goal-to-10',
        tagline: 'Find your first 10 customers',
        price: 197,
        priceLabel: 'R197',
        period: '/mo',
        icon: Rocket,
        color: 'blue',
        features: [
            '1-page professional website',
            'Business discovery tools',
            'Customer acquisition framework',
            'Sales pipeline basics',
            'WhatsApp support',
        ],
        cta: 'Start Sprint',
        popular: false,
    },
    {
        id: 'goal100',
        name: 'Goal-to-100',
        tagline: 'Scale to product-market fit',
        price: 497,
        priceLabel: 'R497',
        period: '/mo',
        icon: Star,
        color: 'indigo',
        features: [
            '3-page professional website',
            'Advanced sales automation',
            'Marketing playbooks',
            'Customer success tracking',
            'Priority WhatsApp support',
        ],
        cta: 'Scale Up',
        popular: true,
    },
    {
        id: 'goal1k',
        name: 'Goal-to-1K',
        tagline: 'Build your team & lead',
        price: 997,
        priceLabel: 'R997',
        period: '/mo',
        icon: Crown,
        color: 'purple',
        features: [
            '5-page professional website',
            'Team management tools',
            'Hiring & delegation framework',
            'Advanced analytics dashboard',
            'Dedicated support channel',
        ],
        cta: 'Go Pro',
        popular: false,
    },
    {
        id: 'goal10k',
        name: 'Goal-to-10K',
        tagline: 'Corporate conglomerate',
        price: 1497,
        priceLabel: 'R1,497',
        period: '/mo',
        icon: Building2,
        color: 'amber',
        features: [
            '8+ page professional website',
            'Multiple revenue streams',
            'Enterprise automation',
            'White-label options',
            'Executive coaching access',
        ],
        cta: 'Go Enterprise',
        popular: false,
    },
];

export const GoalUpsell: React.FC<GoalUpsellProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Retention' || client.currentStage === 'Growth' || client.currentStage === 'Customer';
    const [selectedTier, setSelectedTier] = useState<string | null>(null);
    const [status, setStatus] = useState<'idle' | 'processing' | 'sent' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    if (!isEligible) return null;

    const handlePresent = async (tier: typeof PROGRAM_TIERS[0]) => {
        setSelectedTier(tier.id);
        setStatus('processing');
        try {
            const result = await cjmsApi.chooseProgram({
                leadId: client.id,
                email: client.email,
                programId: tier.id,
                programName: tier.name,
                price: tier.price,
            });
            if (result.success) {
                setStatus('sent');
                feedbackBus.emit('activity_completed', {
                    activityLabel: `Program selected: ${tier.name} (R${tier.price}/mo)`,
                    clientEmail: client.email,
                });
            } else {
                setStatus('error');
                setErrorMsg(result.error || 'Failed to record selection');
            }
        } catch {
            setStatus('error');
            setErrorMsg('Network error — please try again');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-bold text-slate-800">Program Selection</h3>
                <span className="text-[10px] text-blue-600 font-medium ml-auto">
                    {client.company || client.name}
                </span>
            </div>
            <div className="p-4 space-y-4">
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <Target className="w-3 h-3 text-blue-600" />
                    <p className="text-[10px] text-blue-700">
                        Recommend the optimal tier based on their goals, then present the offer
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PROGRAM_TIERS.map(tier => {
                        const Icon = tier.icon;
                        const isSelected = selectedTier === tier.id;
                        return (
                            <div
                                key={tier.id}
                                className={`p-4 rounded-xl border-2 transition-all ${
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50'
                                        : tier.popular
                                            ? 'border-indigo-300 bg-indigo-50/30'
                                            : 'border-slate-200 bg-white hover:border-blue-200'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className={`w-4 h-4 text-${tier.color}-600`} />
                                    <h4 className="text-xs font-bold text-slate-800">{tier.name}</h4>
                                    {tier.popular && (
                                        <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium">
                                            Popular
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-slate-500 mb-2">{tier.tagline}</p>
                                <div className="mb-3">
                                    <span className="text-lg font-bold text-blue-600">{tier.priceLabel}</span>
                                    {tier.period && <span className="text-[10px] text-slate-500">{tier.period}</span>}
                                </div>
                                <ul className="space-y-1 mb-3">
                                    {tier.features.map((f, i) => (
                                        <li key={i} className="text-[10px] text-slate-600 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                {isSelected && status === 'processing' && (
                                    <div className="w-full px-3 py-1.5 text-[11px] font-medium bg-blue-100 text-blue-600 rounded-md text-center">
                                        Recording selection...
                                    </div>
                                )}
                                {!isSelected && status === 'idle' && (
                                    <button
                                        onClick={() => handlePresent(tier)}
                                        className="w-full px-3 py-1.5 text-[11px] font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                                    >
                                        <Zap className="w-3 h-3" />
                                        {tier.cta}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                {status === 'sent' && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-700 font-medium">
                            Program selection recorded — client status updated to {client.currentStage === 'Retention' || client.currentStage === 'Growth' ? 'Onboarding_Ready' : 'Product_Selection'}
                        </span>
                        <ArrowRight className="w-3 h-3 text-green-500 ml-auto" />
                    </div>
                )}
                {status === 'error' && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
                        <span className="text-xs text-red-700 font-medium">{errorMsg}</span>
                        <button onClick={() => setStatus('idle')} className="text-[10px] text-red-600 underline ml-auto">Retry</button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
